import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { FullScreenLoader } from '@/components/ui/Loader';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/auth/AuthProvider';
import { useAccessQuery, type AccessPermission, type AccessResource } from '@/api/bi/access';

interface AccessContextValue {
  isReady: boolean;
  isLoading: boolean;
  isError: boolean;
  isSuperuser: boolean;
  hasAnyViewableResource: boolean;
  resources: AccessResource[];
  resourceBySlug: (slug: string) => AccessResource | undefined;
  can: (slug: string | undefined | null, permission?: AccessPermission) => boolean;
  refetch: () => void;
}

const EMPTY: AccessContextValue = {
  isReady: false,
  isLoading: false,
  isError: false,
  isSuperuser: false,
  hasAnyViewableResource: false,
  resources: [],
  resourceBySlug: () => undefined,
  can: () => false,
  refetch: () => undefined,
};

const AccessContext = createContext<AccessContextValue>(EMPTY);

export function AccessProvider({ children }: { children: ReactNode }) {
  const { ready: authReady, isAuthenticated, user } = useAuth();
  const query = useAccessQuery(authReady && isAuthenticated);

  const value = useMemo<AccessContextValue>(() => {
    const resources = query.data?.resources ?? [];
    const bySlug = new Map(resources.map((r) => [r.slug, r]));
    const isSuperuser = Boolean(user?.is_superuser || query.data?.is_superuser);

    function can(slug: string | undefined | null, permission: AccessPermission = 'view') {
      if (isSuperuser) return true;
      if (!slug) return false;
      if (slug === 'settings' && permission === 'view' && Boolean(query.data?.has_any_viewable_resource)) return true;
      return Boolean(bySlug.get(slug)?.permissions?.[permission]);
    }

    return {
      isReady: authReady && (!isAuthenticated || query.isFetched),
      isLoading: query.isLoading || (authReady && isAuthenticated && !query.isFetched),
      isError: query.isError,
      isSuperuser,
      hasAnyViewableResource: isSuperuser || Boolean(query.data?.has_any_viewable_resource),
      resources,
      resourceBySlug: (slug) => bySlug.get(slug),
      can,
      refetch: () => {
        void query.refetch();
      },
    };
  }, [authReady, isAuthenticated, query, user?.is_superuser]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useAccess(): AccessContextValue {
  return useContext(AccessContext);
}

export function ResourceGate({
  resource,
  permission = 'view',
  fallback = null,
  children,
}: {
  resource: string;
  permission?: AccessPermission;
  fallback?: ReactNode;
  children: ReactNode;
}) {
  const { isReady, can } = useAccess();
  if (!isReady) return <FullScreenLoader label="Loading access" />;
  if (!can(resource, permission)) return <>{fallback}</>;
  return <>{children}</>;
}

export function AccessLoadError() {
  const { refetch } = useAccess();
  return (
    <div className="grid min-h-screen place-items-center px-6">
      <div className="max-w-md text-center">
        <h1 className="font-display text-2xl font-bold tracking-tight">Could not load BI access</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          We could not confirm your permissions. Try again, or ask a superuser to check your BI assignments.
        </p>
        <Button className="mt-6" variant="primary" onClick={refetch}>Retry</Button>
      </div>
    </div>
  );
}
